import type { AxiosRequestConfig } from 'axios';

import { omit } from 'lodash';
import { srmInstance } from '@/shared/api/api-instances/srm/api-instance';
import type {
  CreateDashboardTab,
  CreateTab,
  DashboardTab,
  DashboardTabBrief,
  UpdateTab,
} from '../model/tabs';

export class DashboardTabsApi {
  static getDashboardTabsList(options: AxiosRequestConfig = {}) {
    return srmInstance.get<DashboardTabBrief[]>('/tab/brief', options);
  }

  static getDashboardTabsBasket(options?: AxiosRequestConfig) {
    return srmInstance.get<DashboardTabBrief[]>('/tab/basket', options);
  }

  static getDashboardTab(id: DashboardTab['id'], options?: AxiosRequestConfig) {
    return srmInstance.get<DashboardTab>(`/tab/${id}`, options);
  }

  static resetDashboardTab(
    id: DashboardTab['id'],
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.put<DashboardTab>(`/tab/${id}/reset`, options);
  }

  static replaceDashboardWithTab(
    id: DashboardTab['id'],
    payload?: Pick<DashboardTab, 'config' | 'widgets'>,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.put<DashboardTab>(
      `/tab/${id}/dashboard`,
      payload,
      options,
    );
  }

  static updateDashboardTabParams(
    id: string,
    tab: DashboardTab,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.put<DashboardTab>(`/tab/${id}/params`, tab, options);
  }

  static updateDashboardTabConfig(
    id: string,
    config: DashboardTab['config'],
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.put<DashboardTab>(
      `/tab/config`,
      {
        id,
        config,
      },
      options,
    );
  }

  static deleteDashboardTab(
    tabId: string,
    to_basket: boolean = true,
    options: AxiosRequestConfig = {},
  ) {
    return srmInstance.delete(`/tab/${tabId}`, {
      ...options,
      params: {
        to_basket,
      },
    });
  }

  static createDashboardTab(
    name: string,
    tab: Omit<CreateDashboardTab, 'dashboard_name'>,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post<DashboardTab>(
      `/tab/dashboard/${name}`,
      omit(tab, ['dashboard_name']),
      options,
    );
  }

  static createTab(tab: CreateTab, options?: AxiosRequestConfig) {
    return srmInstance.post<DashboardTab>('/tab', tab, options);
  }

  static updateTab(tab: UpdateTab, options?: AxiosRequestConfig) {
    return srmInstance.patch<DashboardTab>(`/tab/config`, tab, options);
  }

  static restoreTab(tabId: string, options?: AxiosRequestConfig) {
    return srmInstance.put<DashboardTab>(
      `/tab/${tabId}/restore`,
      undefined,
      options,
    );
  }
}